import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  Store as StoreIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { getEventById, updateEvent, deleteEvent } from '../services/eventService';
import { getEventApplications, updateApplicationStatus } from '../services/applicationService';

const EventManagement = () => {
  const theme = useTheme();
  const { eventId } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editedEvent, setEditedEvent] = useState(null);
  const [error, setError] = useState('');

  // Fetch event details
  const { data: event, isLoading: eventLoading, error: eventError } = useQuery(
    ['event', eventId],
    () => getEventById(eventId),
    {
      onSuccess: (data) => {
        setEditedEvent(data);
      },
      onError: (error) => {
        setError(error.message);
      }
    }
  );

  // Fetch applications
  const { data: applications, isLoading: applicationsLoading } = useQuery(
    ['eventApplications', eventId],
    () => getEventApplications(eventId)
  );

  // Update event mutation
  const updateEventMutation = useMutation(
    (data) => updateEvent(eventId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event', eventId]);
        setEditDialogOpen(false);
      },
      onError: (error) => {
        setError(error.message);
      },
    }
  );

  // Delete event mutation
  const deleteEventMutation = useMutation(
    () => deleteEvent(eventId),
    {
      onSuccess: () => {
        history.push('/my-events');
      },
      onError: (error) => {
        setError(error.message);
      },
    }
  );

  // Update application status mutation
  const updateApplicationMutation = useMutation(updateApplicationStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['eventApplications', eventId]);
    },
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    updateEventMutation.mutate(editedEvent);
  };

  const handleDeleteConfirm = () => {
    deleteEventMutation.mutate();
  };

  const handleApplicationStatusUpdate = (applicationId, status) => {
    updateApplicationMutation.mutate({ applicationId, status });
  };

  if (eventLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (eventError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error loading event: {eventError.message}
        </Alert>
      </Container>
    );
  }

  const stats = {
    totalApplications: applications?.length || 0,
    acceptedApplications: applications?.filter(app => app.status === 'accepted').length || 0,
    pendingApplications: applications?.filter(app => app.status === 'pending').length || 0,
    rejectedApplications: applications?.filter(app => app.status === 'rejected').length || 0,
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {/* Header Section */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3,
          background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" component="h1" gutterBottom>
              {event.title}
            </Typography>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Chip
                icon={<EventIcon />}
                label={new Date(event.date).toLocaleDateString()}
                variant="outlined"
              />
              <Chip
                icon={<LocationIcon />}
                label={event.location}
                variant="outlined"
              />
              <Chip
                icon={<PeopleIcon />}
                label={`Capacity: ${event.capacity}`}
                variant="outlined"
              />
              {event.category_ids.map((categoryId) => {
                const category = categories.find(c => c.id === categoryId);
                return (
                  <Chip
                    key={categoryId}
                    icon={<CategoryIcon />}
                    label={category?.name || 'Unknown'}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit Event
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Event
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6">Total Applications</Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 2 }}>
                {stats.totalApplications}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6">Accepted</Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 2 }}>
                {stats.acceptedApplications}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <AccessTimeIcon color="warning" />
                <Typography variant="h6">Pending</Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 2 }}>
                {stats.pendingApplications}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: alpha(theme.palette.error.main, 0.1) }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CancelIcon color="error" />
                <Typography variant="h6">Rejected</Typography>
              </Box>
              <Typography variant="h4" sx={{ mt: 2 }}>
                {stats.rejectedApplications}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Applications" />
          <Tab label="Event Details" />
          <Tab label="Statistics" />
        </Tabs>

        {/* Applications Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {applicationsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : applications?.length === 0 ? (
              <Alert severity="info">No applications yet</Alert>
            ) : (
              <List>
                {applications.map((application) => (
                  <React.Fragment key={application.id}>
                    <ListItem
                      sx={{
                        bgcolor: alpha(
                          application.status === 'accepted'
                            ? theme.palette.success.main
                            : application.status === 'rejected'
                            ? theme.palette.error.main
                            : theme.palette.warning.main,
                          0.1
                        ),
                        mb: 1,
                        borderRadius: 1,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar>
                          <StoreIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={application.vendor_name}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              size="small"
                              label={application.status}
                              color={
                                application.status === 'accepted'
                                  ? 'success'
                                  : application.status === 'rejected'
                                  ? 'error'
                                  : 'warning'
                              }
                            />
                            <Typography variant="body2" color="text.secondary">
                              Applied: {new Date(application.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      {application.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleApplicationStatusUpdate(application.id, 'accepted')}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleApplicationStatusUpdate(application.id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Event Details Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {event.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Location Details
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Typography variant="body1">
                        <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {event.location}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Coordinates: {event.latitude}, {event.longitude}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Event Images
                    </Typography>
                    <Grid container spacing={2}>
                      {event.images.map((image, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <img
                            src={image.url}
                            alt={`Event image ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Statistics Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Application Trends
                    </Typography>
                    {/* Add charts/graphs here */}
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        Application trends visualization coming soon
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Category Distribution
                    </Typography>
                    {/* Add charts/graphs here */}
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        Category distribution visualization coming soon
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Edit Event Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleEditSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Event Title"
                  name="title"
                  value={editedEvent?.title || ''}
                  onChange={(e) => setEditedEvent({ ...editedEvent, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  name="description"
                  value={editedEvent?.description || ''}
                  onChange={(e) => setEditedEvent({ ...editedEvent, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Event Date"
                  name="date"
                  value={editedEvent?.date || ''}
                  onChange={(e) => setEditedEvent({ ...editedEvent, date: e.target.value })}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Capacity"
                  name="capacity"
                  value={editedEvent?.capacity || ''}
                  onChange={(e) => setEditedEvent({ ...editedEvent, capacity: e.target.value })}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={updateEventMutation.isLoading}
          >
            {updateEventMutation.isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Event Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this event? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteEventMutation.isLoading}
          >
            {deleteEventMutation.isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Add categories array
const categories = [
  { id: 1, name: 'Food & Beverage' },
  { id: 2, name: 'Entertainment' },
  { id: 3, name: 'Art & Crafts' },
  { id: 4, name: 'Technology' },
  { id: 5, name: 'Fashion' },
];

export default EventManagement; 